import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { USER_ID_PLACEHOLDER } from '../auth/user-id.interceptor';
import { ChargerOverview, ChargersOverviewResponse, Group, GroupCharger, GroupChargersResponse, GroupDetailResponse, GroupHierarchyResponse, GroupOperationResult, GroupResponse, GroupsResponse } from '../models';

@Injectable({
	providedIn: 'root'
})
export class GroupsService {
	private http = inject(HttpClient);

	getGroups(): Observable<Group[]> {
		return this.http.get<GroupsResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups`)
			.pipe(map(r => r.groups));
	}

	getChildGroups(parentId: string): Observable<Group[]> {
		return this.http.get<GroupsResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups?parentId=${parentId}`)
			.pipe(map(r => r.groups));
	}

	getGroup(groupId: string): Observable<GroupDetailResponse> {
		return this.http.get<GroupDetailResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/${groupId}`);
	}

	getGroupHierarchy(groupId: string): Observable<GroupHierarchyResponse> {
		return this.http.get<GroupHierarchyResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/${groupId}/hierarchy`);
	}

	createGroup(name: string, parentGroupId?: string | null): Observable<Group> {
		const body: { name: string; parentGroupId?: string } = { name };
		if (parentGroupId) body.parentGroupId = parentGroupId;
		return this.http.post<GroupResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups`, body)
			.pipe(map(r => r.group));
	}

	updateGroup(groupId: string, name: string): Observable<GroupOperationResult> {
		return this.http.put<void>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/${groupId}`,
			{ name },
			{ observe: 'response' }
		).pipe(
			map(r => ({ success: r.status === 200 })),
			catchError(err => of({ success: false, errorMessage: err.error?.error ?? err.message }))
		);
	}

	deleteGroup(groupId: string): Observable<GroupOperationResult> {
		return this.http.delete<void>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/${groupId}`,
			{ observe: 'response' }
		).pipe(
			map(r => ({ success: r.status === 200 })),
			catchError(err => of({ success: false, errorMessage: err.error?.error ?? err.message }))
		);
	}

	moveGroup(groupId: string, newParentId: string | null): Observable<GroupOperationResult> {
		return this.http.put<void>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/${groupId}/move`,
			{ newParentId },
			{ observe: 'response' }
		).pipe(
			map(r => ({ success: r.status === 200 })),
			catchError(err => of({ success: false, errorMessage: err.error?.error ?? err.message }))
		);
	}

	assignChargersToGroup(groupId: string, chargerIds: string[]): Observable<GroupOperationResult> {
		return this.http.post<void>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/${groupId}/chargers`,
			{ chargerIds },
			{ observe: 'response' }
		).pipe(
			map(r => ({ success: r.status === 200 })),
			catchError(err => of({ success: false, errorMessage: err.error?.error ?? err.message }))
		);
	}

	getGroupChargers(groupId: string, includeDescendants = false): Observable<GroupCharger[]> {
		const params = includeDescendants ? '?includeDescendants=true' : '';
		return this.http.get<GroupChargersResponse>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/${groupId}/chargers${params}`
		).pipe(map(r => r.chargers));
	}

	getGroupChargersOverview(groupId: string): Observable<ChargerOverview[]> {
		return this.http
			.get<ChargersOverviewResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/${groupId}/chargers/status?id=overview`)
			.pipe(
				map(r => r.chargers),
				catchError((error: HttpErrorResponse) => {
					if (error.status === 404) return of([]);
					return throwError(() => error);
				})
			);
	}

	getUngroupedChargers(): Observable<GroupCharger[]> {
		return this.http.get<GroupChargersResponse>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/ungrouped/chargers`
		).pipe(map(r => r.chargers));
	}

	getUngroupedChargersOverview(limit?: number, nextToken?: string): Observable<ChargersOverviewResponse> {
		let url = `${environment.api}/users/${USER_ID_PLACEHOLDER}/groups/ungrouped/chargers/status?id=overview`;
		if (limit != null) url += `&limit=${limit}`;
		if (nextToken) url += `&nextToken=${encodeURIComponent(nextToken)}`;
		return this.http
			.get<ChargersOverviewResponse>(url)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					if (error.status === 404) return of({ chargers: [], nextToken: null });
					return throwError(() => error);
				})
			);
	}

	removeChargerFromGroup(chargerId: string, groupId: string): Observable<GroupOperationResult> {
		return this.http.delete<void>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${chargerId}/groups`,
			{ body: { groupId }, observe: 'response' }
		).pipe(
			map(r => ({ success: r.status === 200 })),
			catchError(err => of({ success: false, errorMessage: err.error?.error ?? err.message }))
		);
	}
}
